import React, { useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const keyframeCSS = `
  @keyframes revealSparkle {
    0% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 1; }
    100% { transform: translate(var(--tx), var(--ty)) rotate(720deg) scale(0); opacity: 0; }
  }
  @keyframes revealPulseRing {
    0% { transform: scale(0.8); opacity: 0.6; border-width: 3px; }
    100% { transform: scale(3); opacity: 0; border-width: 1px; }
  }
  @keyframes spotlightReveal {
    0% { opacity: 0; transform: scale(0.5); }
    40% { opacity: 1; transform: scale(1.2); }
    100% { opacity: 0.8; transform: scale(1); }
  }
  @keyframes continueFade {
    0%, 60% { opacity: 0; }
    100% { opacity: 0.6; }
  }
  @keyframes shimmerText {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes confettiFall {
    0% { transform: translate(var(--cx, 0), -20px) rotate(0deg) scale(1); opacity: 1; }
    80% { opacity: 1; }
    100% { transform: translate(var(--cx2, 20px), var(--cy, 400px)) rotate(var(--cr, 720deg)) scale(0.5); opacity: 0; }
  }
  @keyframes revealStageLight {
    0% { opacity: 0; }
    20% { opacity: 0.5; }
    40% { opacity: 1; }
    60% { opacity: 0.7; }
    100% { opacity: 0.5; }
  }
  @keyframes revealFloorGlow {
    0% { opacity: 0; transform: scaleX(0.3); }
    50% { opacity: 0.6; transform: scaleX(1.2); }
    100% { opacity: 0.4; transform: scaleX(1); }
  }
  @keyframes boxIconBounce {
    0% { transform: translateY(0) scale(1); }
    30% { transform: translateY(-15px) scale(1.1); }
    50% { transform: translateY(0) scale(1); }
    70% { transform: translateY(-8px) scale(1.05); }
    100% { transform: translateY(0) scale(1); }
  }
  @keyframes labelGlow {
    0%, 100% { text-shadow: 0 0 10px rgba(200,200,220,0.3); }
    50% { text-shadow: 0 0 20px rgba(200,200,220,0.5), 0 0 40px rgba(200,200,220,0.2); }
  }
`;

function formatValue(value) {
  if (typeof value === 'number') {
    return value.toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: value < 1 ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }
  return String(value);
}

function getValueTier(value) {
  if (typeof value !== 'number') return 'low';
  if (value >= 10000) return 'high';
  if (value >= 100) return 'medium';
  return 'low';
}

function getTierColors(tier) {
  switch (tier) {
    case 'high':
      return {
        text: '#D4AF37',
        glow: 'rgba(212,175,55,0.8)',
        bgRadial: 'radial-gradient(ellipse at center, rgba(212,175,55,0.2) 0%, rgba(100,50,0,0.06) 35%, transparent 65%)',
        shadow: '0 0 40px rgba(212,175,55,0.7), 0 0 80px rgba(212,175,55,0.4), 0 0 140px rgba(212,175,55,0.2)',
        particle: '#F0D060',
        confetti: ['#D4AF37', '#F0D060', '#FFD700', '#FFF8DC', '#B8860B'],
      };
    case 'medium':
      return {
        text: '#F0A030',
        glow: 'rgba(240,160,48,0.6)',
        bgRadial: 'radial-gradient(ellipse at center, rgba(240,160,48,0.12) 0%, transparent 55%)',
        shadow: '0 0 30px rgba(240,160,48,0.5), 0 0 60px rgba(240,160,48,0.2)',
        particle: '#F0C060',
        confetti: ['#F0A030', '#F0C060', '#FFA500', '#FFD700', '#FF8C00'],
      };
    default:
      return {
        text: '#3498db',
        glow: 'rgba(52,152,219,0.5)',
        bgRadial: 'radial-gradient(ellipse at center, rgba(52,152,219,0.1) 0%, transparent 55%)',
        shadow: '0 0 25px rgba(52,152,219,0.4), 0 0 50px rgba(52,152,219,0.2)',
        particle: '#5dade2',
        confetti: ['#3498db', '#5dade2', '#85C1E9', '#AED6F1', '#2980B9'],
      };
  }
}

function Sparkle({ delay, tx, ty, size, color, duration }) {
  return (
    <div
      style={{
        position: 'absolute',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: size > 4 ? '2px' : '50%',
        backgroundColor: color,
        boxShadow: `0 0 ${size * 3}px ${color}`,
        animation: `revealSparkle ${duration}s ease-out ${delay}s forwards`,
        '--tx': `${tx}px`,
        '--ty': `${ty}px`,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    />
  );
}

function Confetti({ colors, count = 40 }) {
  const items = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 1.5,
      duration: 2 + Math.random() * 2,
      cx: `${(Math.random() - 0.5) * 100}px`,
      cx2: `${(Math.random() - 0.5) * 150}px`,
      cy: `${300 + Math.random() * 300}px`,
      cr: `${(Math.random() - 0.5) * 1440}deg`,
      width: 4 + Math.random() * 6,
      height: 3 + Math.random() * 4,
      shape: Math.random() > 0.5 ? 'circle' : 'rect',
    }));
  }, [colors, count]);

  return (
    <>
      {items.map((c) => (
        <div
          key={c.id}
          style={{
            position: 'absolute',
            top: '-10px',
            left: c.left,
            width: `${c.width}px`,
            height: c.shape === 'circle' ? `${c.width}px` : `${c.height}px`,
            borderRadius: c.shape === 'circle' ? '50%' : '1px',
            backgroundColor: c.color,
            animation: `confettiFall ${c.duration}s ease-in ${c.delay}s forwards`,
            '--cx': c.cx,
            '--cx2': c.cx2,
            '--cy': c.cy,
            '--cr': c.cr,
            opacity: 0,
            animationFillMode: 'forwards',
            pointerEvents: 'none',
            zIndex: 15,
            boxShadow: `0 0 3px ${c.color}`,
          }}
        />
      ))}
    </>
  );
}

export default function RevealAnimation({ isVisible, boxNumber, prizeValue, prizeLabel, onComplete, autoClose = false }) {
  useEffect(() => {
    if (!isVisible || !autoClose) return;
    const timer = setTimeout(() => {
      onComplete?.();
    }, 2500);
    return () => clearTimeout(timer);
  }, [isVisible, autoClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPaccoNero = prizeLabel === 'SCATOLA MISTERIOSA';
  const displayText = !isPaccoNero && prizeLabel && (prizeValue === 0 || prizeValue === undefined) ? prizeLabel : null;
  const tier = isPaccoNero ? getValueTier(prizeValue) : displayText ? 'medium' : getValueTier(prizeValue);
  const colors = isPaccoNero
    ? { text: '#ccc', glow: 'rgba(150,150,150,0.6)', bgRadial: 'radial-gradient(ellipse at center, rgba(50,50,50,0.15) 0%, transparent 55%)', shadow: '0 0 30px rgba(100,100,100,0.4), 0 0 60px rgba(50,50,50,0.2)', particle: '#aaa', confetti: ['#333', '#666', '#999', '#ccc', '#D4AF37'] }
    : getTierColors(tier);

  const sparkles = useMemo(() => {
    const items = [];
    const count = tier === 'high' ? 24 : tier === 'medium' ? 14 : 8;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const dist = 80 + Math.random() * 160;
      items.push({
        id: i,
        tx: Math.cos(angle) * dist,
        ty: Math.sin(angle) * dist - 40,
        size: 2 + Math.random() * 6,
        delay: 0.3 + Math.random() * 0.6,
        duration: 0.8 + Math.random() * 1,
      });
    }
    return items;
  }, [tier]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(2, 2, 8, 0.94)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
            cursor: autoClose ? 'default' : 'pointer',
            overflow: 'hidden',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={autoClose ? undefined : onComplete}
        >
          <style>{keyframeCSS}</style>

          {/* Stage spotlights from top */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: '20%',
            width: '60%',
            height: '100%',
            background: `
              radial-gradient(ellipse 30% 60% at 30% 0%, rgba(255,255,255,0.03) 0%, transparent 70%),
              radial-gradient(ellipse 30% 60% at 70% 0%, rgba(255,255,255,0.03) 0%, transparent 70%)
            `,
            animation: 'revealStageLight 2s ease-out forwards',
            pointerEvents: 'none',
          }} />

          {/* Spotlight on prize */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: colors.bgRadial,
            animation: 'spotlightReveal 1.2s ease-out forwards',
            pointerEvents: 'none',
          }} />

          {/* Floor glow */}
          <div style={{
            position: 'absolute',
            bottom: '15%',
            left: '10%',
            right: '10%',
            height: '3px',
            background: `linear-gradient(90deg, transparent, ${colors.glow}, transparent)`,
            animation: 'revealFloorGlow 1s ease-out 0.5s forwards',
            opacity: 0,
            filter: 'blur(2px)',
            pointerEvents: 'none',
          }} />

          {/* Confetti (high prizes) */}
          {tier === 'high' && <Confetti colors={colors.confetti} count={50} />}
          {tier === 'medium' && <Confetti colors={colors.confetti} count={25} />}

          {/* Box number label */}
          <motion.div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
              fontWeight: 600,
              color: 'rgba(200,200,220,0.85)',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '20px',
              zIndex: 5,
              animation: 'labelGlow 2s ease-in-out infinite',
            }}
            initial={{ opacity: 0, y: -40, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.5, ease: 'easeOut' }}
          >
            Pacco n. {boxNumber}
          </motion.div>

          {/* Box icon */}
          <motion.div
            style={{
              fontSize: '2rem',
              marginBottom: '12px',
              zIndex: 5,
              animation: 'boxIconBounce 1s ease-in-out 0.6s',
            }}
            initial={{ opacity: 0, scale: 0, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
          >
            📦
          </motion.div>

          {/* Prize value with effects */}
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
          }}>
            {/* Burst rings */}
            {[0.3, 0.6, 0.9].map((delay, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  border: `2px solid ${colors.glow}`,
                  animation: `revealPulseRing 1.8s ease-out ${delay}s forwards`,
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              />
            ))}

            {/* Sparkle particles */}
            {sparkles.map((s) => (
              <Sparkle
                key={s.id}
                tx={s.tx}
                ty={s.ty}
                size={s.size}
                delay={s.delay}
                duration={s.duration}
                color={colors.particle}
              />
            ))}

            {/* SCATOLA MISTERIOSA label (shown before the value) */}
            {isPaccoNero && (
              <motion.div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
                  fontWeight: 700,
                  color: '#fff',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  textShadow: '0 0 20px rgba(100,100,100,0.5), 0 0 40px rgba(0,0,0,0.8)',
                  zIndex: 5,
                  position: 'relative',
                  marginBottom: '8px',
                  background: 'linear-gradient(90deg, #666, #ccc, #fff, #ccc, #666)',
                  backgroundSize: '300% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shimmerText 3s linear infinite',
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.3 }}
              >
                SCATOLA MISTERIOSA
              </motion.div>
            )}

            {/* Prize amount */}
            <motion.div
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: isPaccoNero
                  ? 'clamp(2rem, 7vw, 4rem)'
                  : tier === 'high' ? 'clamp(3.5rem, 12vw, 7rem)' : 'clamp(3rem, 9vw, 5.5rem)',
                fontWeight: 700,
                letterSpacing: '0.02em',
                color: isPaccoNero ? '#D4AF37' : colors.text,
                textShadow: isPaccoNero
                  ? '0 0 30px rgba(212,175,55,0.6), 0 0 60px rgba(212,175,55,0.3)'
                  : colors.shadow,
                zIndex: 5,
                position: 'relative',
                ...(!isPaccoNero && tier === 'high' ? {
                  background: 'linear-gradient(90deg, #8B6914, #D4AF37, #F0D060, #FFE88A, #F0D060, #D4AF37, #8B6914)',
                  backgroundSize: '300% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shimmerText 2.5s linear infinite',
                  filter: 'drop-shadow(0 0 30px rgba(212,175,55,0.5))',
                } : {}),
              }}
              initial={{ scale: 0, rotate: -10, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 150,
                damping: 10,
                delay: isPaccoNero ? 0.8 : 0.4,
              }}
            >
              {isPaccoNero ? formatValue(prizeValue) : (displayText || formatValue(prizeValue))}
            </motion.div>
          </div>

          {/* Tier label */}
          {tier === 'high' && (
            <motion.div
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1rem',
                fontWeight: 600,
                color: '#D4AF37',
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                marginTop: '16px',
                zIndex: 5,
                textShadow: '0 0 15px rgba(212,175,55,0.5)',
              }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              GRAN PREMIO!
            </motion.div>
          )}

          {/* Continue label */}
          {!autoClose && (
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: '0.85rem',
              color: 'rgba(160,160,192,0.5)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginTop: '50px',
              animation: 'continueFade 2.5s ease-in forwards',
              zIndex: 5,
            }}>
              Tocca per continuare
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
